import { Link } from 'react-router-dom';

const modules = [
  {
    title: 'Projects',
    description: 'Create and manage user stories, requirements, and project documentation',
    icon: 'ri-folder-3-line',
    href: '/app/projects/management',
    color: 'bg-blue-500',
    stats: { total: 12, active: 8 }
  },
  {
    title: 'Form Builder',
    description: 'Design responsive forms with drag-and-drop components',
    icon: 'ri-file-list-3-line',
    href: '/app/projects/form-builder',
    color: 'bg-green-500',
    stats: { total: 25, active: 18 }
  },
  {
    title: 'Workflow Builder',
    description: 'Create automated workflows and business processes',
    icon: 'ri-git-branch-line',
    href: '/app/projects/workflow-builder',
    color: 'bg-purple-500',
    stats: { total: 15, active: 12 }
  },
  {
    title: 'Database Relations',
    description: 'Visualize and manage database relationships and queries',
    icon: 'ri-database-2-line',
    href: '#',
    color: 'bg-orange-500',
    stats: { total: 8, active: 6 }
  },
  {
    title: 'Connection Settings',
    description: 'Configure and manage external system integrations',
    icon: 'ri-plug-line',
    href: '#',
    color: 'bg-red-500',
    stats: { total: 11, active: 9 }
  },
  {
    title: 'Template Management',
    description: 'Create and manage reusable templates for all communication channels',
    icon: 'ri-file-text-line',
    href: '#',
    color: 'bg-teal-500',
    stats: { total: 24, active: 16 }
  },
  {
    title: 'PDF Editor',
    description: 'Design and customize PDF templates with visual tools',
    icon: 'ri-file-pdf-line',
    href: '#',
    color: 'bg-indigo-500',
    stats: { total: 18, active: 14 }
  }
];

export default function Dashboard() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back!
          </h1>
          <p className="text-lg text-gray-600">
            Here's what's happening with your workflow automation today.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-bar-chart-line text-blue-600 text-xl"></i>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Projects</p>
                <p className="text-2xl font-bold text-gray-900">8</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completed Tasks</p>
                <p className="text-2xl font-bold text-gray-900">143</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-purple-600 text-xl"></i>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">27</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <i className="ri-team-line text-orange-600 text-xl"></i>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Team Members</p>
                <p className="text-2xl font-bold text-gray-900">24</p>
              </div>
            </div>
          </div>
        </div>

        {/* Modules Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Modules</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {modules.map((module) => (
              <Link
                key={module.title}
                to={module.href}
                className="group bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`${module.color} w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <i className={`${module.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-xs text-gray-500">Total</span>
                    <span className="text-lg font-bold text-gray-900">{module.stats.total}</span>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {module.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {module.description}
                </p>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-gray-600">{module.stats.active} active</span>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 group-hover:text-blue-600 transition-colors"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Projects</h3>
            <div className="space-y-3">
              {['E-commerce Platform', 'Mobile Banking App', 'CRM Dashboard'].map((project, idx) => (
                <div key={project} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-folder-line text-blue-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{project}</p>
                      <p className="text-xs text-gray-500">Updated 2 hours ago</p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-400"></i>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {[
                { action: 'Created new form', time: '10 minutes ago', icon: 'ri-file-list-line', color: 'green' },
                { action: 'Updated workflow', time: '1 hour ago', icon: 'ri-git-branch-line', color: 'purple' },
                { action: 'Completed project', time: '3 hours ago', icon: 'ri-check-line', color: 'blue' }
              ].map((activity, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-8 h-8 bg-${activity.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${activity.icon} text-${activity.color}-600`}></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
